import {
	AccountData,
	CardData,
	ApiStatus,
	CreateCardRequest,
	LoginData,
	LoginRequest,
	LoginResponse,
	LoginType,
	PagedData,
	TransactionData,
	CardLimits,
	CardServices,
	CardServicesRequest,
	UserData,
	BasicCardOperationResponse,
	CardProgramResponse,
	CardStatus,
	DisputeRequest,
	InitiateChangePasswordResponse,
	ChangePasswordResponse,
	Corporation,
	CorporationMember,
	AccessTokenResponse,
} from './Models'
import { clearSession, getCurrentUserId, getTenantId, setCurrentUserId, setTenantId } from './Session'
import axios from 'axios'
import Qs from 'qs'
import { getCustomerApi, getTenantName } from '../settings'

interface ErrorData {
	error: string
	errorMessage: string
}

const REALM_NAME = getTenantName()
const CUSTOMER_API = getCustomerApi()

const USERS_API_URL = 'https://users.easypsit.eu/api/users'
const CORPORATIONS_API_URL = 'https://users.easypsit.eu/api/corporations'
const CARDS_API_URL = 'https://cards.easypsit.eu/api/cards'
const DISPUTES_API_URL = 'https://cards.easypsit.eu/api/disputes'
const ACCOUNTS_API_URL = 'https://accounts.easypsit.eu/api/accounts'
const TENANT_API_URL = 'https://tenant.easypsit.eu/api/tenants'

// Flag to track if a token refresh is in progress
let isRefreshing = false
// Queue of requests to retry after token refresh
let refreshSubscribers: Array<(token: string) => void> = []

const ax = axios.create({
	headers: {
		'Content-Type': 'application/json-patch+json',
		'Access-Control-Allow-Origin': '*',
	},
	paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: 'repeat' }),
})

// Function to refresh the token
const refreshAccessToken = async (): Promise<string | null> => {
	try {
		const refreshToken = localStorage.getItem('refreshToken')
		if (!refreshToken) {
			return null
		}

		const response = await axios.post<{ data: AccessTokenResponse }>(
			`${USERS_API_URL}/refresh-token`,
			{
				refreshToken,
			},
			{
				headers: {
					'Content-Type': 'application/json',
					'x-realm-id': getTenantId(),
				},
			}
		)

		const tokenData = response.data.data

		// Save the new tokens
		localStorage.setItem('token', tokenData.access_token as string)
		localStorage.setItem('refreshToken', tokenData.refresh_token as string)

		return tokenData.access_token as string
	} catch (error) {
		console.error('Error refreshing token:', error)
		// If refresh fails, log the user out
		clearSession()
		localStorage.removeItem('token')
		localStorage.removeItem('refreshToken')
		window.location.href = `/?callback=${window.location.pathname}${window.location.search}`
		return null
	}
}

// Function to add request to queue
const subscribeTokenRefresh = (callback: (token: string) => void) => {
	refreshSubscribers.push(callback)
}

// Function to process queue with new token
const onTokenRefreshed = (token: string) => {
	refreshSubscribers.forEach((callback) => callback(token))
	refreshSubscribers = []
}

// Request interceptor - adds token to requests
ax.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem('token')
		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}
		return config
	},
	(error) => {
		return Promise.reject(error)
	}
)

// Response interceptor - handles token refresh
ax.interceptors.response.use(
	(response) => {
		return response
	},
	async (error) => {
		const originalRequest = error.config

		// If the error is due to an expired token (401) and we haven't tried to refresh yet
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				// If a refresh is already in progress, add this request to the queue
				return new Promise<string>((resolve) => {
					subscribeTokenRefresh((token: string) => {
						originalRequest.headers.Authorization = `Bearer ${token}`
						resolve(ax(originalRequest))
					})
				})
			}

			// Mark that we're starting a refresh
			originalRequest._retry = true
			isRefreshing = true

			try {
				// Try to refresh the token
				const newToken = await refreshAccessToken()

				if (newToken) {
					// Update the original request with the new token
					originalRequest.headers.Authorization = `Bearer ${newToken}`

					// Process any queued requests
					onTokenRefreshed(newToken)

					// Reset the refreshing flag
					isRefreshing = false

					// Retry the original request
					return ax(originalRequest)
				}
			} catch (refreshError) {
				console.error('Error during token refresh:', refreshError)
			} finally {
				isRefreshing = false
			}
		}

		return Promise.reject(error)
	}
)

interface ApiResult<ResponseT> {
	success: boolean
	data?: ResponseT
	code: number | string
	error?: string
	errorMessage?: string
}

enum FilterOperation {
	Equal = 'Equal',
	NotEqual = 'NotEqual',
	Greater = 'Greater',
	Less = 'Less',
	GreaterOrEqual = 'GreaterOrEqual',
	LessOrEqual = 'LessOrEqual',
	Contains = 'Contains',
	NotContains = 'NotContains',
	StartsWith = 'StartsWith',
	EndsWith = 'EndsWith',
	NotStartsWith = 'NotStartsWith',
	NotEndsWith = 'NotEndsWith',
}

enum TransactionType {
	Pos = 'Pos',
	Ecommerce = 'Ecommerce',
	Atm = 'Atm',
	PosCash = 'PosCash',
	Fee = 'Fee',
	AnnualFee = 'AnnualFee',
	Manual = 'Manual',
	Preauthorization = 'Preauthorization',
	PinChange = 'PinChange',
	Balance = 'Balance',
	FastCash = 'FastCash',
	PinSelection = 'PinSelection',
	PaperPin = 'PaperPin',
}

enum ProcessingTypeRequest {
	Debit = 'Debit',
	Credit = 'Credit',
	DebitReversal = 'DebitReversal',
	CreditReversal = 'CreditReversal',
}

enum AccountType {
	Current = 'Current',
	Personal = 'Personal',
	StaffCurrent = 'StaffCurrent',
	Card = 'Card',
	FxTransit = 'FxTransit',
	LoanPayment = 'LoanPayment',
	PrepaidCard = 'PrepaidCard',
	CashDesk = 'CashDesk',
}

export interface TransactionFilter {
	OrderBy?: string
	OrderByDescending?: boolean
	'OffsetPaging.Skip'?: number
	'OffsetPaging.Take'?: number
	'CreatedOn.StartDate'?: string
	'CreatedOn.EndDate'?: string
	TransactionType?: TransactionType
	TransactionId?: string
	ProcessingType?: ProcessingTypeRequest
	Id?: string
	TenantId?: string
	UserId?: string
	CardId?: string
	AccountId?: string
	MerchantId?: string
	'Amount.Value'?: number
	'Amount.Operation'?: FilterOperation
	Currency?: string
	TransactionStatus?: string
	'ReferenceNumber.Value'?: string
	'ReferenceNumber.Operation'?: FilterOperation
	'MerchantCategoryCode.Value'?: string
	'MerchantCategoryCode.Operation'?: FilterOperation
	'SettlementDate.StartDate'?: string
	'SettlementDate.EndDate'?: string
	SendSms?: boolean
}

const put = async <RequestT, ResponseT>(
	route: string,
	data: RequestT | null,
	extraHeaders?: any
): Promise<ApiResult<ResponseT>> => {
	try {
		const headers: any = {}

		if (extraHeaders) {
			Object.assign(headers, extraHeaders)
		}

		const response = await ax.put<any>(route, data, { headers })
		return {
			success: true,
			code: response.status,
			data: response.data.data,
		}
	} catch (error: any) {
		console.error('Error:', error)
		if (error.response) {
			// The request was made and the server responded with a status code
			// that falls out of the range of 2xx
			if (error.response.status === 401) {
				handeResponse401()
			}

			return {
				success: false,
				code: error.response.status,
				error: error.response.data?.message || error.message,
			}
		} else if (error.request) {
			// The request was made but no response was received
			return {
				success: false,
				code: 'NETWORK_ERROR',
				error: 'Network error, no response received',
			}
		} else {
			// Something happened in setting up the request that triggered an Error
			return {
				success: false,
				code: 'REQUEST_SETUP_ERROR',
				error: error.message,
			}
		}
	}
}

const post = async <RequestT, ResponseT>(
	route: string,
	data: RequestT | null,
	extraHeaders?: any
): Promise<ApiResult<ResponseT>> => {
	try {
		const headers: any = {}

		if (extraHeaders) {
			Object.assign(headers, extraHeaders)
		}

		// Special case for change-password endpoint
		if (route === `${USERS_API_URL}/change-password`) {
			// Don't add the token for this specific endpoint
			delete ax.defaults.headers.common['Authorization']
		}

		const response = await ax.post<any>(route, data, { headers })

		// Restore the Authorization header if it was removed
		if (route === `${USERS_API_URL}/change-password`) {
			const token = localStorage.getItem('token')
			if (token) {
				ax.defaults.headers.common['Authorization'] = `Bearer ${token}`
			}
		}

		// Handle image response
		const contentType = response.headers['content-type']
		if (contentType && contentType.startsWith('image')) {
			return {
				success: true,
				data: response.data as ResponseT,
				code: response.status,
			}
		}

		return {
			success: true,
			code: response.status,
			data: response.data.data,
		}
	} catch (error: any) {
		console.error('Error:', error)
		if (error.response) {
			if (error.response.status === 401) {
				handeResponse401()
			}


			const errorData = !error.response.data.data
				? getError(error.response.data)
				: getError(error.response.data.data);
			// getError(error.response.data);
			// console.log("errorData", error.response.data.data)

			return {
				success: false,
				code: error.response.status,
				error: errorData.error,
				errorMessage: errorData.errorMessage,
				data: error.response.data,
			}
		} else if (error.request) {
			return {
				success: false,
				code: 'NETWORK_ERROR',
				error: 'Network error, no response received',
			}
		} else {
			return {
				success: false,
				code: 'REQUEST_SETUP_ERROR',
				error: error.message,
			}
		}
	}
}

function handeResponse401() {
	clearSession()
	localStorage.removeItem('token')
	localStorage.removeItem('refreshToken')
	window.location.href = `/?callback=${window.location.pathname}${window.location.search}`
	return {
		success: false,
		code: 403,
		error: 'Unauthorized',
	}
}


function getError(responseData: any): ErrorData {
	console.log("getError", responseData)
	if (Array.isArray(responseData)) {
		return getError(responseData[0])
	} else if (responseData.failures) {
		return {
			error: responseData.failures[0].code.name,
			errorMessage: responseData.failures[0].reason
		}
	} else if (responseData.status?.messages) {
		return {
			error: responseData.status?.messages[0].code,
			errorMessage: responseData.status?.messages[0].description
		}
	} else if (responseData.messages) {
		return {
			error: responseData.messages[0].code.name,
			errorMessage: responseData.messages[0].code.name
		}
	} else if (responseData.name && responseData.reason) {
		return {
			error: responseData.name,
			errorMessage: responseData.reason
		}
	} else if (responseData.message) {
		return {
			error: responseData.message,
			errorMessage: responseData.message
		}
	} else {
		return {
			error: 'Unknown error',
			errorMessage: 'Unknown error'
		}
	}
}

const get = async <ResponseT,>(
	route: string,
	params?: any | null,
	headers?: any | null,
	specialJsonTreatment?: (i: string) => string
): Promise<ApiResult<ResponseT>> => {
	try {
		// Process array parameters
		const processedParams: Record<string, any> = {}
		if (params) {
			for (const key in params) {
				if (params[key] === undefined) {
					continue
				}

				// if (Array.isArray(params[key])) {
				// 	const len = params[key].length
				// 	for (let i = 0; i < len; i++) {
				// 		processedParams[key] = params[key][i].toString()
				// 	}
				// } else {
				// // Handle array parameters
				if (Array.isArray(params[key])) {
					// Axios handles arrays in params differently than fetch
					// We'll keep the same parameter name for each array item
					processedParams[key] = params[key]
				} else {
					processedParams[key] = params[key]
				}
			}
		}

		const requestHeaders = headers || {}

		const response = await ax.get<any>(route, {
			params: processedParams,
			headers: requestHeaders,
			transformResponse: [
				(data) => {
					// Handle empty response
					if (!data) {
						return { data: null }
					}

					// Handle special JSON treatment
					if (specialJsonTreatment && typeof data === 'string') {
						const processedText = specialJsonTreatment(data)
						return JSON.parse(processedText)
					}

					// Handle normal JSON response
					if (typeof data === 'string') {
						try {
							return JSON.parse(data)
						} catch (e) {
							return data
						}
					}

					return data
				},
			],
		})

		// Handle image response
		const contentType = response.headers['content-type']
		if (contentType && contentType.startsWith('image')) {
			return {
				success: true,
				data: response.data as ResponseT,
				code: response.status,
			}
		}

		return {
			success: true,
			code: response.status,
			data: response.data.data,
		}
	} catch (error: any) {
		console.error('Error:', error)
		if (error.response) {
			if (error.response.status === 401) {
				handeResponse401()
			}

			return {
				success: false,
				code: error.response.status,
				error: error.response.data?.message || error.message,
			}
		} else if (error.request) {
			return {
				success: false,
				code: 'NETWORK_ERROR',
				error: 'Network error, no response received',
			}
		} else {
			return {
				success: false,
				code: 'REQUEST_SETUP_ERROR',
				error: error.message,
			}
		}
	}
}

const defaultCardStatuses: CardStatus[] = [
	CardStatus.AwaitingApproval,
	//CardStatus.Requested,
	//CardStatus.InActive,
	CardStatus.Active,
	CardStatus.BlockedByCustomer,
	CardStatus.BlockedByCreditor,
	CardStatus.BlockedByIssuer,
	CardStatus.BlockedByCardProcessor,
	CardStatus.Lost,
	CardStatus.Stolen,
	CardStatus.Damaged,
	CardStatus.Closed,
	CardStatus.Renewed,
	CardStatus.Expired,
	CardStatus.Misappropriation,
	//CardStatus.UnauthorizedUse,
]

class Api {
	readonly Cards = {
		CreateCard: async (cardRequest: CreateCardRequest): Promise<ApiResult<any>> => {
			return post(`${CARDS_API_URL}/new`, cardRequest)
		},
		IssueCard: async (cardRequest: CreateCardRequest): Promise<ApiResult<any>> => {
			return post(`${CARDS_API_URL}/issue`, cardRequest)
		},
		GetCards: async (filters?: any): Promise<ApiResult<PagedData<CardData>>> => {
			if (!filters || !('OrderBy' in filters)) {
				filters = { ...filters, OrderBy: 'createdOn' }
			}
			return get(`${CARDS_API_URL}`, { ...filters, 'Filters.Statuses': defaultCardStatuses })
			//return get(`${CARDS_API_URL}`, filters)
		},
		GetCardsWithBalance: async (filters?: any): Promise<ApiResult<PagedData<CardData>>> => {
			return API.Cards.GetCards({ ...filters, withBalance: true })
		},
		GetCardUsageLimits: async (cardId: string): Promise<ApiResult<CardLimits>> => {
			return get(`${CARDS_API_URL}/${cardId}/card-limits`)
		},
		SetCardUsageLimits: async (cardId: string, limits: CardLimits): Promise<ApiResult<any>> => {
			return put(`${CARDS_API_URL}/${cardId}/card-limits`, limits)
		},
		GetCardServices: async (cardId: string): Promise<ApiResult<CardServices>> => {
			const headers = {
				'x-user-id': getCurrentUserId(),
				'x-realm-id': getTenantId(),
			}
			return get(`${CARDS_API_URL}/${cardId}/toggle-services`, null, headers)
		},
		SetCardServices: async (cardId: string, request: CardServicesRequest): Promise<ApiResult<CardServices>> => {
			return put(`${CARDS_API_URL}/${cardId}/toggle-services`, request)
		},
		SetCardServicesTest: async (cardId: string, request: CardServices): Promise<ApiResult<CardServices>> => {
			return put(`${CARDS_API_URL}/${cardId}/toggle-services`, request)
		},
		GetUserCards: async (): Promise<ApiResult<any>> => {
			const currentUserId = getCurrentUserId().toString()
			return get(`${CARDS_API_URL}/transactions`, {
				'Filters.UserId': currentUserId,
			})
		},
		GetTransactions: async (filters?: TransactionFilter): Promise<ApiResult<PagedData<TransactionData>>> => {
			return get(`${CARDS_API_URL}/transactions`, { ...filters })
		},
		GetAuthorizations: async (
			page: number,
			filters?: TransactionFilter
		): Promise<ApiResult<PagedData<TransactionData>>> => {
			return get(`${CARDS_API_URL}/authorizations`, { page: page, ...filters })
		},
		Freeze: async (cardId: string): Promise<ApiResult<BasicCardOperationResponse>> => {
			return put(`${CARDS_API_URL}/${cardId}/set-card-status`, {
				reason: 'Frozen from client by user: ' + getCurrentUserId(),
				cardStatus: 'BlockedByCustomer',
			})
		},
		ReportStolen: async (cardId: string): Promise<ApiResult<BasicCardOperationResponse>> => {
			return put(`${CARDS_API_URL}/${cardId}/set-card-status`, {
				reason: 'Reported stolen from client by user: ' + getCurrentUserId(),
				cardStatus: 'Stolen',
			})
		},
		Close: async (cardId: string): Promise<ApiResult<BasicCardOperationResponse>> => {
			return put(`${CARDS_API_URL}/${cardId}/set-card-status`, {
				reason: 'Closed by user: ' + getCurrentUserId(),
				cardStatus: 'Closed',
			})
		},
		Unfreeze: async (cardId: string): Promise<ApiResult<BasicCardOperationResponse>> => {
			//return put(`${CARDS_API_URL}/${cardId}/freeze`, {})
			return put(`${CARDS_API_URL}/${cardId}/set-card-status`, {
				reason: 'Unfrozen from client by user: ' + getCurrentUserId(),
				cardStatus: 'Active',
			})
		},
		CreateDispute: async (cardId: string, disputeData: DisputeRequest): Promise<ApiResult<any>> => {
			return post(`${DISPUTES_API_URL}/cards/${cardId}`, disputeData)
		},
		GetDispute: async (cardId: string): Promise<ApiResult<any>> => {
			return get(`${DISPUTES_API_URL}/cards/${cardId}`)
		},
		SearchDisputes: async (cardId: string): Promise<ApiResult<any>> => {
			return get(`${DISPUTES_API_URL}/cards/${cardId}`)
		},
		UpdateCardAlias: async (cardId: string, alias: string): Promise<ApiResult<any>> => {
			return put(`${CARDS_API_URL}/${cardId}/alias`, { alias: alias })
		}
	}

	readonly Accounts = {
		OpenNewAccount: async (idempotencyKey: string): Promise<ApiResult<any>> => {
			return post(
				`${ACCOUNTS_API_URL}/`,
				{
					withIban: false,
					name: 'First account',
					accountType: AccountType.Card,
					currency: 'BGN',
					userId: getCurrentUserId(),
				},
				{
					'X-Idempotency-Key': idempotencyKey,
				}
			)
		},
		GetActiveAccounts: async (): Promise<ApiResult<PagedData<AccountData>>> => {
			const currentUserId = getCurrentUserId().toString()

			// const replaceNumberValuesWithString = (input: string) => {
			// 	return input.replace(/"id":(\d+),/g, '"id":"$1",')
			// }

			return get(
				`${ACCOUNTS_API_URL}/`,
				{ 'Filters.UserId': currentUserId, 'Filters.Status': 'Active' }
				// replaceNumberValuesWithString
			)
		},
		GetAccountDataById: async (id: string): Promise<ApiResult<AccountData>> => {
			return get(`${ACCOUNTS_API_URL}/${id}`)
		},
		GetTransactions: async (
			accountId: string,
			page: number,
			filters?: TransactionFilter
		): Promise<ApiResult<PagedData<TransactionData>>> => {
			return get(`${ACCOUNTS_API_URL}/${accountId}/transactions`, {
				...filters,
				AccountId: accountId,
				'Filters.OrderBy': 'TransactionDate',
			})
		},
		GetBalance: async (
			accountId: string,
			filters?: TransactionFilter
		): Promise<ApiResult<PagedData<TransactionData>>> => {
			return get(`${ACCOUNTS_API_URL}/balance`, {
				...filters,
				AccountId: accountId,
			})
		},
		GenerateAccountStatements: async (accountId: string, from: Date, to: Date): Promise<ApiResult<any>> => {
			return post(
				`${ACCOUNTS_API_URL}/${accountId}/statement`,
				{ fromDate: from.toISOString(), toDate: to.toISOString() },
				{
					'X-Idempotency-Key': `Idem-${Math.ceil(Math.random() * 100000)}-${Math.ceil(Math.random() * 100000)}`,
				}
			)
		},
		GetAccountStatements: async (accountId: string, statementId: string): Promise<ApiResult<any>> => {
			return get<any>(`${ACCOUNTS_API_URL}/${accountId}/statement/${statementId}`)
		},
		Search: async (iban: string): Promise<ApiResult<any>> => {
			return get(`${ACCOUNTS_API_URL}/search`, {
				tenantId: getTenantId(),
				iban: iban,
			})
		},
	}

	readonly Users = {
		Login: async (request: LoginData): Promise<ApiResult<LoginResponse>> => {
			const filledRequest: LoginRequest = {
				userName: request.userName,
				password: request.password,
				realmName: REALM_NAME,
				clientSecret: CUSTOMER_API,
				loginType: LoginType.email,
				clientId: 'customer-api',
				deviceId: null,
			}

			return post<LoginRequest, LoginResponse>(`${USERS_API_URL}/login`, filledRequest).then((response) => {
				if (response === undefined) {
					return {
						success: false,
						code: 400,
						error: 'No response',
					}
				}

				if (response.success) {
					const data = response.data as LoginResponse
					localStorage.setItem('token', data.accessTokenResponse?.access_token as string)
					localStorage.setItem('refreshToken', data.accessTokenResponse?.refresh_token as string)
					// const token = data.accessTokenResponse!.access_token as string
					// const decoded = jwtDecode(token)
					// console.log('Decoded token', decoded)

					setCurrentUserId(data.userId as string)
					setTenantId(data.tenantId as string)

					return {
						success: true,
						code: 200,
						data: data,
					}
				}

				const error =
					response.error === 'InvalidUserCredentials' ? 'Invalid username or password' : response.error

				return {
					success: false,
					code: response.code,
					error: error,
					data: response.data,
					errorMessage: response.errorMessage,
				}
			})
		},
		InitiateMFA: async (email: string, password: string): Promise<ApiResult<Blob>> => {
			const data = {
				loginType: LoginType.email,
				clientId: 'customer-api',
				userName: email,
				password: password,
				realmName: REALM_NAME,
			}

			//TODO: Change this for prod
			return post(`${USERS_API_URL}/initiate-insecure-mfa`, data)
		},
		ValidateMFA(email: string, password: string, totp: string) {
			throw 'Not implemented'
		},
		InitiateForgotPassword: async (email: string): Promise<ApiResult<InitiateChangePasswordResponse>> => {
			const data = {
				email: email,
				tenantName: REALM_NAME,
			}

			return post(`${USERS_API_URL}/initiate-password-reset`, data)
		},
		ResetPassword: async (
			otp: string,
			tempPassword: string,
			newPassword: any,
			tokenId: string,
			userId: string,
			tenantId: string
		): Promise<ApiResult<ChangePasswordResponse>> => {
			const data = {
				otp: otp,
				temporaryPassword: tempPassword,
				newPassword: newPassword,
				confirmPassword: newPassword,
				resetPasswordTokenId: tokenId,
			}

			const extraHeaders = {
				//"x-user-id" : userId,
				'x-realm-id': tenantId,
			}

			return post(`${USERS_API_URL}/change-password`, data, extraHeaders)
		},
		GetRoles: async (): Promise<any> => {
			return get(`${USERS_API_URL}/role-mappings`, null)
		},
		GetUsers: async (params?: any): Promise<ApiResult<PagedData<UserData>>> => {
			return get(`${USERS_API_URL}/`, { ...params, UserUserId: '123123' }) //, headersWithRealmId())
		},
		GetUser: async (userId: string, params?: any): Promise<ApiResult<UserData>> => {
			try {
				const result = await get<PagedData<UserData>>(`${USERS_API_URL}/`, { ...params, UserUserId: userId })
				return extractFirst(result)
			} catch (err) {
				return new Promise((resolve) =>
					resolve({
						success: false,
						code: 400,
						error: err?.toString(),
					})
				)
			}
		},
		GetCustomers: async (): Promise<any> => {
			return get(`${USERS_API_URL}/customers`, null)
		},
		GetCustomerProfileImage: async (): Promise<any> => {
			return get(`${USERS_API_URL}/customers/profile-image`, null)
		},
	}

	readonly Corporations = {
		GetCorporations: async (skip: number, take: number, params?: any): Promise<ApiResult<PagedData<Corporation>>> => {
			return get(`${CORPORATIONS_API_URL}`, { ...params, 'OffsetPaging.Skip': skip, 'OffsetPaging.Take': take })
		},
		GetCorporationMembers: async (
			corporationId: string,
			skip: number,
			take: number,
			filters?: any
		): Promise<ApiResult<PagedData<CorporationMember>>> => {
			if (!filters || !('OrderBy' in filters)) {
				filters = { ...filters, OrderBy: 'createdOn' }
			}

			if (!('OrderByDescending' in filters)) {
				filters = { ...filters, OrderByDescending: true }
			}

			filters = { ...filters, 'OffsetPaging.Skip': skip }
			filters = { ...filters, 'OffsetPaging.Take': take }

			return get(`${CORPORATIONS_API_URL}/${corporationId}/members`, filters)
		},
		GetCorporationMember: async (
			corporationId: string,
			userId: string,
			filters?: any
		): Promise<ApiResult<CorporationMember>> => {
			if (!filters || !('OrderBy' in filters)) {
				filters = { ...filters, OrderBy: 'createdOn' }
			}

			if (!('OrderByDescending' in filters)) {
				filters = { ...filters, OrderByDescending: true }
			}

			filters = { ...filters, 'OffsetPaging.Skip': 0, 'OffsetPaging.Take': 1, MemberUserId: userId }

			try {
				const result = await get<PagedData<CorporationMember>>(
					`${CORPORATIONS_API_URL}/${corporationId}/members`,
					filters
				)
				return new Promise((resolve) => resolve(extractFirst(result)))
			} catch (err) {
				throw err
			}
		},
	}

	readonly Tenant = {
		GetCardPrograms: async (): Promise<ApiResult<PagedData<CardProgramResponse>>> => {
			return get(`${TENANT_API_URL}/${getTenantId()}/card-programs`)
		},
	}

	//https://users.easypsit.eu/api/users/logout
	Logout = async (): Promise<ApiResult<any>> => {
		const promise = get<ApiStatus>(`${USERS_API_URL}/logout`, null)
		clearSession()
		localStorage.removeItem('token')
		localStorage.removeItem('refreshToken')
		return promise
	}
}

function extractFirst<T>(result: ApiResult<PagedData<T>>): ApiResult<T> {
	if (result.success && result.data && result.data.data) {
		if (result.data.data.length > 0) {
			return {
				success: true,
				code: 200,
				data: result.data!.data[0],
			}
		} else {
			return {
				success: true,
				code: 404,
				error: 'Member not found',
			}
		}
	}

	return {
		success: false,
		code: result.code,
		error: !!result.error ? result.error : 'Unknown Error',
	}
}

// function headersWithRealmId(headers?: any | null) {
// 	if (headers === undefined || headers === null) {
// 		return { 'x-realm-id': getTenantId() }
// 	}
//
// 	return { ...headers, 'x-realm-id': getTenantId() }
// }
//
// function headersWithRealmIdAndUserId(headers: any | undefined | null) {
// 	if (headers === undefined || headers === null) {
// 		return { 'x-realm-id': getTenantId(), 'x-user-id': getCurrentUserId() }
// 	}
//
// 	return { ...headers, 'x-realm-id': getTenantId(), 'x-user-id': getCurrentUserId() }
// }

const API = new Api()
export default API
